import React, { useMemo, useEffect, useCallback } from 'react'
import styled from 'particles/themes/styled'
import { Calendar, ChevronDown, X } from 'react-feather'
import { useField, useFormikContext } from 'formik'
import {
    DragDropContext,
    Droppable,
    Draggable,
    DroppableProvided,
    DropResult,
    DraggableProvided,
    DraggingStyle,
} from 'react-beautiful-dnd'
import Select, { StylesConfig } from 'react-select'
import DatePicker, { ReactDatePickerProps } from 'react-datepicker'
import { Box, Flex, Text, Image, FlexProps, SxStyleProp } from 'rebass'
import _, { noop } from 'lodash'
import makeAnimated from 'react-select/animated'
import { theme, ThemeColorKey } from '../theme'
import { buildMediaUrl } from '../services/cloudinary'
import './datePicker.css'
import { parseBackendDate } from 'services/dates'
import { Checkbox as RebassCheckbox, Label as RebassLabel } from '@rebass/forms'
import emojiRegex from 'emoji-regex'
import NumberInputComponent, { NumberInputSize } from 'particles/NumberInput'
import { FormikTextArea } from 'particles/TextInputs/TextArea'
import { FormikTextField } from 'particles/TextInputs/TextField'

export enum DROPDOWN_STYLE_TYPE {
    FILTER = 'filter',
    STANDARD = 'standard',
}

const style: { [key: string]: StylesConfig } = {
    filter: {
        control: (styles: any) => {
            return {
                ...styles,
                borderColor: theme.colors.astronaut,
            }
        },
        dropdownIndicator: (styles: any) => {
            return {
                ...styles,
                color: theme.colors.astronaut,
            }
        },
        indicatorSeparator: (styles: any) => {
            return {
                ...styles,
                backgroundColor: theme.colors.astronaut,
            }
        },
        groupHeading: (styles: any) => ({
            ...styles,
            color: theme.colors.astronaut,
            fontWeight: 'bold',
            paddingLeft: 20,
        }),
        option: (styles: any, { isDisabled = false }: { isDisabled: boolean }) => ({
            ...styles,
            cursor: isDisabled ? 'not-allowed' : 'default',
            color: isDisabled ? theme.colors.silver : theme.colors.astronaut,
            paddingLeft: 40,
            ':hover': isDisabled
                ? ''
                : {
                      backgroundColor: theme.colors.astronaut,
                      color: theme.newColors.neutral50,
                  },
            ':active': isDisabled
                ? ''
                : {
                      backgroundColor: theme.colors.astronaut,
                      color: theme.newColors.neutral50,
                  },
        }),
        multiValue: (styles: any) => {
            return {
                ...styles,
                backgroundColor: theme.colors.astronaut,
            }
        },
        multiValueLabel: (styles: any) => ({
            ...styles,
            color: theme.newColors.neutral50,
        }),
        multiValueRemove: (styles: any) => ({
            ...styles,
            color: theme.newColors.neutral50,
            ':hover': {
                backgroundColor: theme.colors.astronaut,
                color: theme.newColors.neutral50,
            },
        }),
    },
    standard: {
        singleValue: (provided, state) => ({
            ...provided,
            color: theme.colors.mineShaft,
        }),
        placeholder: (provided, state) => ({
            ...provided,
            color: theme.colors.doveGrey,
        }),
        control: (provided, state) => ({
            ...provided,
            minHeight: '33px',
            height: '33px',
            borderRadius: '4px',
            border: `1px solid ${state.selectProps.error ? theme.colors.monza : theme.colors.doveGrey}`,
            opacity: state.isDisabled ? '0.6' : undefined,
            '&:hover': {
                borderColor: state.selectProps.error
                    ? theme.colors.monza
                    : state.isFocused
                      ? theme.colors.governorBay
                      : theme.colors.doveGrey,
            },
            boxShadow: 'none',
        }),
        indicatorsContainer: (provided, state) => ({
            ...provided,
            color: theme.colors.doveGrey,
        }),
        indicatorSeparator: (provided, state) => ({
            ...provided,
            margin: 0,
            backgroundColor: state.selectProps.error ? theme.colors.monza : theme.colors.doveGrey,
        }),
        container: (provided, state) => ({
            ...provided,
            width: state.selectProps.width,
        }),
        dropdownIndicator: (provided, state) => ({
            ...provided,
            padding: 0,
        }),
        clearIndicator: (provided, state) => ({
            ...provided,
            padding: 6,
        }),
        menuList: (provided, state) => ({
            ...provided,
            width: '100%',
            border: `1px solid ${theme.colors.governorBay}`,
            borderRadius: 5,
            '::-webkit-scrollbar': {
                width: theme.spacing.xxxs,
            },
            '::-webkit-scrollbar-thumb': {
                backgroundColor: theme.colors.governorBay,
                borderRadius: 5,
            },
            '::-webkit-scrollbar-track': {
                backgroundColor: theme.colors.hawkesBlue,
                borderRadius: 5,
            },
        }),
        groupHeading: (provided, state) => ({
            ...provided,
            color: theme.colors.governorBay,
            fontSize: '1em',
            fontWeight: 'bold',
            paddingBottom: theme.spacing.xxxs,
        }),
        option: (provided, state) => ({
            ...provided,
            textAlign: 'left',
            paddingLeft: theme.spacing.s,
            color: state.isSelected
                ? theme.newColors.neutral50
                : state.isDisabled
                  ? theme.colors.iron
                  : theme.colors.governorBay,
            backgroundColor: state.isSelected ? theme.colors.governorBay : undefined,
            '&:hover': {
                backgroundColor: !state.isSelected ? theme.colors.hawkesBlue : undefined,
            },
        }),
    },
}

export const StyledInput = styled.input<any>`
    width: ${props => (props.width ? props.width : '100%')};
    background: ${props => (props.disabled ? theme.colors.iron : 'transparent')};
    vertical-align: middle;
    -webkit-appearance: none;
    border-radius: 4px;
    border-width: 0px;
    border-style: initial;
    border-color: initial;
    border-image: initial;
    white-space: pre-line;
    resize: none;
    overflow: auto;
    box-shadow: inset 2px 2px 2px rgba(0, 0, 0, 0.25);
    box-sizing: border-box;
    ::-webkit-inner-spin-button,
    ::-webkit-outer-spin-button {
        -webkit-appearance: none;
        -moz-appearance: none;
        appearance: none;
        margin: 0;
    }

    &:disabled {
        cursor: not-allowed;
        opacity: 0.6;
    }

    &:hover:not([disabled]) {
        background: 'none';
    }

    &:focus {
        outline: none;
        border: 0;
    }

    &.error {
        border-bottom: 2px solid ${theme.colors.monza};
    }
`

interface LabelProps {
    name?: string
    text?: string
    showError?: boolean
    fontSize?: string | number
    textAlign?: string
    textColor?: ThemeColorKey
    mt?: string | number
    sx?: SxStyleProp
    required?: boolean
    width?: string
    height?: string
}
const Label: React.FC<LabelProps> = ({
    name,
    text,
    showError,
    fontSize = '0.8em',
    textAlign = 'right',
    textColor = theme.colors.boulder,
    mt = '0',
    sx,
    required,
    width = '100px',
    height,
}) => {
    return text ? (
        <Flex justifyContent={textAlign === 'center' ? 'center' : undefined} alignItems="center" height={height}>
            <label htmlFor={name} style={{ width }}>
                <Text
                    mt={mt}
                    fontSize={fontSize}
                    textAlign={textAlign as any}
                    color={showError ? theme.colors.monza : textColor}
                    sx={sx}
                >
                    {required ? <span style={{ marginRight: 2 }}>*</span> : null}
                    {text && text.toUpperCase()}
                </Text>
            </label>
        </Flex>
    ) : null
}

interface ErrorProps extends Omit<FlexProps, 'key'> {
    name: string
}
const Error: React.FC<ErrorProps> = ({ name, ...props }) => {
    const [, meta] = useField<any>(name)
    const showError = meta.touched && Boolean(meta.error)

    return showError && typeof meta.error === 'string' ? (
        <Flex name={name} mx={theme.spacing.xxxs} {...props}>
            <Text fontSize="0.8em" color={theme.colors.monza}>
                {meta.error}
            </Text>
        </Flex>
    ) : null
}

interface TitleInputProps {
    name: string
    label?: string
    border?: boolean
    fontSize?: string
    required?: boolean
    showInputLength?: boolean
    maxInputLength?: number
}
const TitleInput: React.FC<TitleInputProps> = ({
    name,
    label,
    border = false,
    fontSize = '1em',
    required,
    showInputLength = false,
    maxInputLength = 100,
    ...props
}) => {
    const [field, meta] = useField(name)
    const showError = meta.touched && Boolean(meta.error)

    const getInputCharLength = useCallback(() => {
        const regex = emojiRegex()
        const emojiArr = meta.value.match(regex)
        if (emojiArr != null) {
            return meta.value.length - emojiArr.length
        } else {
            return meta.value.length
        }
    }, [meta])
    return (
        <>
            <Label
                text={label}
                name={name}
                fontSize="0.6em"
                textAlign="center"
                width="100%"
                showError={showError}
                required={required}
            />
            <Box flex={1}>
                <Box style={{ position: 'relative', width: '100%' }}>
                    <StyledInput
                        type="text"
                        {...field}
                        {...props}
                        style={{
                            textAlign: 'center',
                            boxShadow: border ? `${theme.colors.gallery} 0px 0px 0px 1px inset` : 'none',
                            padding: border ? theme.spacing.xxxs : 0,
                            fontSize,
                            paddingRight: showInputLength ? theme.spacing.l : null,
                        }}
                        className={showError ? 'error' : null}
                    />
                    {showInputLength && (
                        <Text
                            fontSize="10px"
                            color={theme.colors.silver}
                            style={{
                                position: 'absolute',
                                top: '9px',
                                right: '7px',
                            }}
                            fontWeight="normal"
                        >
                            {getInputCharLength()} / {maxInputLength}
                        </Text>
                    )}
                </Box>
                <Error name={name} justifyContent="center" fontSize="0.5em" fontWeight="normal" />
            </Box>
        </>
    )
}

interface NumberInputProps extends Omit<React.HTMLProps<HTMLInputElement>, 'as'> {
    name: string
    label?: string
    border?: boolean
    fontSize?: string
    required?: boolean
    inputSize?: NumberInputSize
    disableDefaultStyle?: boolean
    placeholder?: string
    width?: string
}
const NumberInput: React.FC<NumberInputProps> = ({
    name,
    label,
    required,
    inputSize,
    placeholder,
    width,
    disabled,
}) => {
    const [field, meta] = useField(name)

    return (
        <NumberInputComponent
            {...field}
            size={inputSize}
            error={meta.error}
            label={label}
            isRequired={required}
            placeholder={placeholder}
            width={width}
            isDisabled={disabled}
        />
    )
}

export const NumberInputGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing[300]};
`

interface OrderedContentListInWindowInputProps {
    name: string
    dragPosX?: number
    dragPosY?: number
    width?: number
    valueMapper?: (value: any) => any
    setList?: (value: any) => void
}
const OrderedContentListInWindowInput: React.FC<OrderedContentListInWindowInputProps> = ({
    valueMapper,
    setList = noop,
    width = 426,
    dragPosX = 0,
    dragPosY = 0,
    ...props
}) => {
    const [field] = useField<any>(props)
    const { setFieldValue } = useFormikContext()
    const fieldValue = field.value || []

    const reorderValues = (startIndex: number, endIndex: number): void => {
        const newValues = Object.assign([], fieldValue)
        const [removed] = newValues.splice(startIndex, 1)
        newValues.splice(endIndex, 0, removed)
        return newValues
    }

    const onDragEnd = (result: DropResult): void => {
        const { destination, source } = result
        if (!destination) {
            return
        }
        if (destination.droppableId === source.droppableId && destination.index === source.index) {
            return
        }
        const reorderedValues = reorderValues(source.index, destination.index)
        setFieldValue(field.name as never, reorderedValues)
        setList(reorderedValues)
    }

    const ImageItem: React.FC<{
        value: { resource_type: string; public_id: string; source: 'cloudinary' | 'aws' }
        index: number
        totalImgCount: number
    }> = ({ value, index, totalImgCount }) => {
        const cloudinaryUrl = useMemo(
            () =>
                buildMediaUrl(value, value.resource_type === 'video' ? { start_offset: 0, format: 'jpg' } : undefined),
            [value],
        )
        return (
            <Draggable draggableId={value.public_id} index={index} isDragDisabled={totalImgCount === 1}>
                {(provided: DraggableProvided, snapshot): JSX.Element => {
                    const draggableStyle = provided.draggableProps.style as DraggingStyle
                    let transform = draggableStyle && draggableStyle.transform
                    if (snapshot.isDragging) {
                        const regex = new RegExp('translate\\((.*?)px, (.*?)px\\)')
                        const childValues = regex.exec((draggableStyle && draggableStyle.transform) || '')
                        let shiftX = 100
                        if (totalImgCount > 3) {
                            shiftX = width / totalImgCount
                        }
                        if (childValues != null) {
                            const x =
                                dragPosX +
                                index * shiftX +
                                parseFloat(childValues[1]) -
                                parseFloat(draggableStyle && String(draggableStyle.left))
                            const y =
                                dragPosY +
                                parseFloat(childValues[2]) -
                                parseFloat(draggableStyle && String(draggableStyle.top))

                            transform = `translate(${x}px, ${y}px)`
                        }
                    }
                    return (
                        <Box
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            style={{
                                ...provided.draggableProps.style,
                                transform,
                                position: snapshot.isDragging ? 'absolute' : undefined,
                            }}
                            width="25%"
                            m="4px"
                        >
                            <Image src={cloudinaryUrl} />
                        </Box>
                    )
                }}
            </Draggable>
        )
    }

    return (
        <Box mx={0} my={theme.spacing.xxs} sx={{ textAlign: 'center' }}>
            <Error name={props.name} />
            <DragDropContext onDragEnd={onDragEnd}>
                <Droppable droppableId="list" direction="horizontal">
                    {(provided: DroppableProvided): JSX.Element => (
                        <div
                            style={{ display: 'flex', alignItems: 'center' }}
                            ref={provided.innerRef}
                            {...provided.droppableProps}
                        >
                            {fieldValue.map((value: any, index: number) => (
                                <ImageItem
                                    key={value.public_id}
                                    value={valueMapper ? valueMapper(value) : value}
                                    index={index}
                                    totalImgCount={fieldValue.length}
                                />
                            ))}
                            {provided.placeholder}
                        </div>
                    )}
                </Droppable>
            </DragDropContext>
        </Box>
    )
}

interface DateInput extends Omit<ReactDatePickerProps, 'onChange'> {
    name: string
    label?: string
    showTime?: boolean
    required?: boolean
    disabled?: boolean
    onChange?: (date: Date | null) => void
    my?: string | number
    style?: SxStyleProp
    timeIntervals?: number
}
const DateInput: React.FC<DateInput> = ({
    name,
    label,
    showTime,
    onChange,
    required,
    disabled,
    my = theme.spacing.xxs,
    style,
    timeIntervals = 60,
    ...props
}) => {
    const [field, meta] = useField(name)
    const { setFieldValue, setFieldTouched } = useFormikContext()
    const showButton = meta.value && !disabled
    const showError = meta.touched && Boolean(meta.error)
    return (
        <Flex my={my} sx={style}>
            <Box style={{ position: 'relative', width: '100%' }}>
                <Calendar width="18px" height="18px" style={{ position: 'absolute', top: '5px', left: '9px' }} />
                <DatePicker
                    {...field}
                    {...props}
                    wrapperClassName={`datePickerWrapper ${disabled ? 'is_disabled' : ''}`}
                    calendarClassName={showTime ? 'dateTimeCalendar' : undefined}
                    className={showError ? 'error' : ''}
                    selected={(field.value && new Date(field.value as any)) || null}
                    showTimeSelect={showTime ? true : undefined}
                    timeIntervals={showTime ? timeIntervals : undefined}
                    timeCaption={showTime ? 'Time' : undefined}
                    onChange={
                        onChange
                            ? (date: Date | null): void => onChange(date)
                            : (value: Date | null): void =>
                                  setFieldValue(
                                      field.name as never,
                                      value && parseBackendDate(value.toISOString(), undefined, { showTime }),
                                  )
                    }
                    readOnly={disabled}
                    maxDate={new Date('2099/12/31')}
                />
                {showButton ? (
                    <X
                        width="14px"
                        height="14px"
                        style={{
                            position: 'absolute',
                            top: '7px',
                            right: '7px',
                            cursor: disabled ? 'not-allowed' : 'pointer',
                        }}
                        onClick={(): void => {
                            if (disabled) return
                            setFieldValue(field.name as never, null)
                            setFieldTouched(field.name as never, true, false)
                        }}
                    />
                ) : null}
                <Error name={name} />
            </Box>
            <Label text={label} name={name} showError={showError} required={required} height="29px" />
        </Flex>
    )
}

interface CheckboxOption {
    id: string
    label: string
    render?: () => JSX.Element
}
interface CheckboxInputProps extends Omit<FlexProps, 'key'> {
    name: string
    label: string
    options: CheckboxOption[]
    required?: boolean
}
const CheckboxInput: React.FC<CheckboxInputProps> = ({ name, label, options, required, ...props }) => {
    const [field, meta] = useField<null | string>(name)
    const showError = meta.touched && Boolean(meta.error)
    const { setFieldValue } = useFormikContext()

    const handleChecked = (optionId: string): void => {
        setFieldValue(field.name as never, optionId)
    }

    return (
        <Box>
            <Flex my={theme.spacing.xxs} alignItems="center" {...props}>
                {options.map(option => {
                    const { id, label, render } = option
                    const isSelected = field.value === id
                    return (
                        <Box
                            key={option.id}
                            mx={theme.spacing.xxs}
                            sx={{ textAlign: 'center', display: 'inline-block' }}
                            onClick={(): void => handleChecked(id)}
                        >
                            <Text fontSize={3} color={isSelected ? theme.colors.astronaut : theme.colors.boulder}>
                                {label}
                            </Text>
                            {render && render()}
                        </Box>
                    )
                })}
            </Flex>
            <Box>
                <Label text={label} textAlign="center" name={name} showError={showError} required={required} />
                <Error name={name} justifyContent="center" />
            </Box>
        </Box>
    )
}

export interface CheckboxMultipleOption<T> {
    value: T
    label: string
}
export interface CheckboxMultipleProps {
    options: CheckboxMultipleOption<any>[]
    name: string
    isVertical?: boolean
    onClick?: any
}
const CheckboxMultiple: React.FC<CheckboxMultipleProps> = ({ options, name, isVertical = false, onClick }) => {
    const [field, meta] = useField<any[]>(name)
    const { setFieldValue, setFieldTouched } = useFormikContext()
    const fieldValue = field.value || []

    const handleChecked = (optionValue: any): void => {
        let next
        const isExist = fieldValue.some(selected => _.isEqual(selected, optionValue))
        if (isExist) {
            next = fieldValue.filter(selected => !_.isEqual(selected, optionValue))
        } else {
            next = fieldValue.concat([optionValue])
        }
        setFieldValue(field.name as never, next)
        if (!meta.touched) setFieldTouched(field.name as never, true, false)
        if (onClick) onClick(next)
    }

    return (
        <Box>
            <Flex flexWrap={'wrap'} flexDirection={isVertical ? 'column' : 'row'}>
                {options.map((option, idx) => (
                    <Flex key={idx} mr={theme.spacing.xxs} my={theme.spacing.xxxs}>
                        <RebassLabel>
                            <RebassCheckbox
                                checked={fieldValue.some(selected => _.isEqual(selected, option.value))}
                                onChange={(): void => handleChecked(option.value)}
                                color={theme.colors.governorBay}
                            />
                            {option.label}
                        </RebassLabel>
                    </Flex>
                ))}
            </Flex>
            <Error name={name} mx={0} />
        </Box>
    )
}

interface DropdownInputProps {
    name: string
    label?: string
    topLabel?: string
    showErrorStyle?: boolean
    styleType?: DROPDOWN_STYLE_TYPE
    onChange?: (option: any) => void
    onSelect?: (option: any) => void
    defaultOption?: any
    defaultSelected?: any
    options?: any
    isLoading?: boolean
    isDisabled?: boolean
    isMulti?: boolean
    isSearchable?: boolean
    isClearable?: boolean
    placeHolder?: string
    onInputChange?: any
    getOptionLabel?: any
    getOptionValue?: any
    value?: any
    id?: string
    fontSize?: string | number
}
const DropdownInput: React.FC<DropdownInputProps> = ({
    name,
    label,
    topLabel,
    options,
    defaultOption,
    onSelect,
    onInputChange,
    defaultSelected = null,
    isMulti = false,
    onChange,
    styleType = DROPDOWN_STYLE_TYPE.FILTER,
    showErrorStyle = false,
    fontSize = '0.8em',
    ...props
}) => {
    const [field, meta] = useField(name)
    const hasError = Boolean(meta.error)
    const showError = showErrorStyle || (meta.touched && hasError)
    const { setFieldValue, setFieldTouched } = useFormikContext()

    const selectedOption = defaultSelected
        ? defaultSelected
        : options.filter((option: any) => option.value === field.value)[0]

    const handleSelectOption = (option: any): void => {
        if (onChange) {
            onChange(option)
            if (!meta.touched) setFieldTouched(field.name as never, true, false)
            return
        }
        let value
        isMulti ? (value = option) : (value = option.value)
        setFieldValue(field.name as never, value)
        if (onSelect !== undefined) {
            onSelect(value)
        }
    }

    return (
        <Flex my={theme.spacing.xxs}>
            <Box flex={1}>
                <Label
                    text={topLabel}
                    name={name}
                    showError={hasError && showError}
                    textAlign="left"
                    width="100%"
                    sx={{ marginBottom: theme.spacing.xxxxs }}
                    fontSize={fontSize}
                />
                <Select
                    onInputChange={onInputChange}
                    options={options}
                    name={field.name}
                    inputId={field.name}
                    value={selectedOption || defaultOption}
                    onChange={handleSelectOption}
                    onBlur={field.onBlur}
                    isMulti={isMulti}
                    components={
                        styleType === DROPDOWN_STYLE_TYPE.STANDARD
                            ? {
                                  DropdownIndicator(): JSX.Element {
                                      return (
                                          <Box mx={theme.spacing.xxxs} mt={theme.spacing.xxxs}>
                                              <ChevronDown size={15} />
                                          </Box>
                                      )
                                  },
                              }
                            : makeAnimated()
                    }
                    styles={
                        styleType === DROPDOWN_STYLE_TYPE.STANDARD
                            ? style.standard
                            : styleType === DROPDOWN_STYLE_TYPE.FILTER
                              ? style.filter
                              : undefined
                    }
                    error={showError}
                    theme={theme => ({
                        ...theme,
                        colors: {
                            ...theme.colors,
                            primary25: theme.colors.astronaut,
                        },
                    })}
                    {...props}
                />
                <Error name={name} />
            </Box>
            {label && (
                <label htmlFor={name} style={{ width: '100px' }}>
                    <Text
                        fontSize={fontSize}
                        textAlign="right"
                        color={hasError && showError ? theme.colors.monza : theme.colors.boulder}
                    >
                        {label && label.toUpperCase()}
                    </Text>
                </label>
            )}
        </Flex>
    )
}

interface SetValueProps extends React.HTMLProps<HTMLInputElement> {
    name: string
    value: string
}
const SetValue: React.FC<SetValueProps> = ({ name, value, ...props }) => {
    const [field] = useField(name)
    const { setFieldValue } = useFormikContext()

    useEffect(() => {
        setFieldValue(field.name as never, value)
    }, [value])

    return <input style={{ display: 'none' }} {...field} {...props} />
}

const Inputs = {
    Checkbox: CheckboxInput,
    TextField: FormikTextField, // todo: after all other place (ex: forget password page) be replace with TextField, we can remove TextInput component
    TextArea: FormikTextArea,
    Title: TitleInput,
    Number: NumberInput,
    OrderedContentListInWindowInput,
    Dropdown: DropdownInput,
    Date: DateInput,
    SetValue,
    Label,
    CheckboxMultiple,
    Error,
}

export default Inputs
